import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MailOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ForgotPasswordModal from "../../../modals/ForgotPasswordModal";
import illustration from "./assets/login-illustration.png";

const API_URL = process.env.REACT_APP_API_URL ;

function getAdminUser() {
  try {
    const savedUser = localStorage.getItem("adminUser");
    return savedUser ? JSON.parse(savedUser) : null;
  } catch {
    return null;
  }
}

function LoginPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    Email: "",
    Password: "",
  });

  const [touched, setTouched] = useState({
    Email: false,
    Password: false,
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isEmailValid = useMemo(() => {
    return /^\S+@\S+\.\S+$/.test(form.Email.trim());
  }, [form.Email]);

  const isPasswordValid = useMemo(() => {
    return form.Password.length >= 6;
  }, [form.Password]);

  const canSubmit = isEmailValid && isPasswordValid && !loading;

  useEffect(() => {
    document.title = "Admin Login | Practical Infosec";

    const token = localStorage.getItem("token");
    const savedRole = localStorage.getItem("role");
    const adminUser = getAdminUser();

    const role = adminUser?.Role || savedRole;

    if (token && role === "ADMIN") {
      navigate("/admin/admin-work-pi", { replace: true });
      return;
    }

    if (token && role && role !== "ADMIN") {
      navigate("/admin/sign_in", { replace: true });
    }
  }, [navigate]);

  function handleChange(event) {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setError("");
  }

  function handleBlur(event) {
    const { name } = event.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  }

  function validateBeforeSubmit() {
    const nextTouched = {
      Email: true,
      Password: true,
    };

    setTouched(nextTouched);

    if (!isEmailValid) {
      setError("Please enter a valid email address.");
      return false;
    }

    if (!isPasswordValid) {
      setError("Password must be at least 6 characters.");
      return false;
    }

    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateBeforeSubmit()) {
      toast.error("Please enter valid login details.", {
        position: "top-center",
      });
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      Email: form.Email.trim(),
      Password: form.Password,
    };

    try {
      const response = await axios.post(`${API_URL}/user/login`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 15000,
      });

      if (response.data?.success) {
        localStorage.setItem("tempToken", response.data.token);
        localStorage.setItem("tempEmail", payload.Email);

        toast.success("OTP sent to your email.", {
          position: "top-center",
        });

        setTimeout(() => {
          navigate("/admin/verify-otp");
        }, 600);

        return;
      }

      toast.error(response.data?.message || "Invalid email or password.", {
        position: "top-center",
      });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Login failed. Invalid email or password.";

      setError(message);

      toast.error(message, {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="relative flex items-center justify-center min-h-screen px-4 py-8 overflow-hidden bg-slate-950">
      <ToastContainer
        position="top-center"
        autoClose={4500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        toastClassName="!rounded-xl !font-semibold"
      />

      {/* BACKGROUND */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.38),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(220,38,38,0.24),transparent_35%),radial-gradient(circle_at_center,rgba(124,58,237,0.20),transparent_45%)]"></div>

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>

        <div className="absolute w-64 h-64 rounded-full top-20 left-10 bg-cyan-400/20 blur-3xl"></div>
        <div className="absolute rounded-full w-72 h-72 bottom-20 right-10 bg-purple-500/20 blur-3xl"></div>
      </div>

      <section className="relative z-10 grid w-full max-w-6xl overflow-hidden border shadow-2xl rounded-[2rem] border-white/15 bg-white/10 backdrop-blur-2xl lg:grid-cols-[1.05fr_0.95fr]">
        {/* LEFT SIDE */}
        <div className="relative hidden p-8 overflow-hidden lg:block">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.28),transparent_36%)]"></div>

          <div className="relative z-10 flex flex-col justify-between h-full min-h-[640px]">
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Practical Infosec Admin
              </span>

              <h1 className="mt-8 text-5xl font-black leading-tight tracking-tight text-white">
                Secure Admin Access for{" "}
                <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                  PI Operations
                </span>
              </h1>

              <p className="max-w-xl mt-5 text-base font-semibold leading-8 text-slate-300">
                Manage cohorts, course fees, training operations, and platform
                controls from one protected admin workspace.
              </p>
            </div>

            <div className="relative p-5 my-8 border rounded-[2rem] bg-white/10 border-white/15">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-r from-cyan-400/20 via-blue-500/20 to-purple-500/20 blur-2xl"></div>

              <img
                src={illustration}
                alt="Admin login illustration"
                className="relative z-10 object-contain w-full h-[300px]"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                icon={<SafetyCertificateOutlined />}
                title="OTP Secure"
                text="Email verification"
              />
              <FeatureCard
                icon={<UserSwitchOutlined />}
                title="Role Based"
                text="Secure access"
              />
              <FeatureCard
                icon={<ThunderboltOutlined />}
                title="Fast Access"
                text="Admin console"
              />
            </div>
          </div>
        </div>

        {/* RIGHT SIDE FORM */}
        <div className="relative px-5 py-8 bg-white sm:px-8 lg:px-10">
          <div className="max-w-md mx-auto">
            <div className="mb-8 text-center lg:text-left">
              <div className="inline-flex items-center justify-center w-16 h-16 mx-auto text-2xl text-white shadow-xl lg:mx-0 rounded-2xl bg-gradient-to-br from-blue-700 to-cyan-500 shadow-blue-500/30">
                <LockOutlined />
              </div>

              <h2 className="mt-5 text-4xl font-black tracking-tight text-slate-950">
                Admin Login
              </h2>

              <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                Enter your credentials. OTP verification is required before
                accessing the admin dashboard.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* EMAIL */}
              <div>
                <label
                  htmlFor="Email"
                  className="block mb-2 text-sm font-black text-slate-800"
                >
                  Email Address
                </label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                    <MailOutlined />
                  </span>

                  <input
                    type="email"
                    name="Email"
                    id="Email"
                    value={form.Email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="admin@practicalinfosec.com"
                    autoComplete="email"
                    className={`w-full py-4 pl-11 pr-4 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-2xl focus:ring-4 ${
                      !touched.Email
                        ? "border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                        : isEmailValid
                        ? "border-green-500 focus:border-green-500 focus:ring-green-100"
                        : "border-red-500 focus:border-red-500 focus:ring-red-100"
                    }`}
                  />
                </div>

                {touched.Email && !isEmailValid && (
                  <p className="mt-2 text-xs font-semibold text-red-600">
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              {/* PASSWORD */}
              <div>
                <label
                  htmlFor="Password"
                  className="block mb-2 text-sm font-black text-slate-800"
                >
                  Password
                </label>

                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-slate-400">
                    <LockOutlined />
                  </span>

                  <input
                    type={showPassword ? "text" : "password"}
                    name="Password"
                    id="Password"
                    value={form.Password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    className={`w-full py-4 pl-11 pr-12 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-2xl focus:ring-4 ${
                      !touched.Password
                        ? "border-slate-200 focus:border-blue-500 focus:ring-blue-100"
                        : isPasswordValid
                        ? "border-green-500 focus:border-green-500 focus:ring-green-100"
                        : "border-red-500 focus:border-red-500 focus:ring-red-100"
                    }`}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 flex items-center pr-4 transition text-slate-400 hover:text-slate-800"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeInvisibleOutlined className="text-xl" />
                    ) : (
                      <EyeOutlined className="text-xl" />
                    )}
                  </button>
                </div>

                {touched.Password && !isPasswordValid && (
                  <p className="mt-2 text-xs font-semibold text-red-600">
                    Password must be at least 6 characters.
                  </p>
                )}
              </div>

              {/* FORGOT PASSWORD */}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold text-slate-400">
                  Protected admin access
                </p>

                <ForgotPasswordModal
                  trigger={
                    <Button
                      type="link"
                      className="!p-0 !h-auto !text-sm !font-black !text-red-600 hover:!text-red-700"
                    >
                      Forgot password?
                    </Button>
                  }
                />
              </div>

              {error && (
                <div className="p-4 text-sm font-bold text-red-700 border border-red-100 rounded-2xl bg-red-50">
                  {error}
                </div>
              )}

              {/* SUBMIT */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="relative inline-flex items-center justify-center w-full gap-3 overflow-hidden px-6 py-4 text-base font-black text-white transition rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 shadow-[0_0_35px_rgba(37,99,235,0.35)] hover:scale-[1.02] hover:shadow-[0_0_55px_rgba(14,165,233,0.60)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
              >
                <span className="absolute inset-0 transition-transform duration-1000 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent hover:translate-x-full"></span>

                {loading ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></span>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Login Securely
                    <span>→</span>
                  </>
                )}
              </button>
            </form>

            <div className="grid grid-cols-2 gap-3 mt-8">
              <InfoBox title="Step 1" text="Login credentials" />
              <InfoBox title="Step 2" text="OTP verification" />
            </div>

            <p className="mt-8 text-xs font-semibold text-center text-slate-400">
              © {new Date().getFullYear()} Practical Infosec Pvt Ltd. All rights
              reserved.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <div className="p-4 border rounded-2xl bg-white/10 border-white/15">
      <div className="text-2xl text-cyan-300">{icon}</div>
      <p className="mt-3 text-sm font-black text-white">{title}</p>
      <p className="mt-1 text-xs font-semibold text-slate-300">{text}</p>
    </div>
  );
}

function InfoBox({ title, text }) {
  return (
    <div className="p-4 text-center border rounded-2xl bg-slate-50 border-slate-200">
      <p className="text-xs font-black tracking-widest text-blue-700 uppercase">
        {title}
      </p>
      <p className="mt-1 text-xs font-bold text-slate-600">{text}</p>
    </div>
  );
}

export default LoginPage;